import $store from '../../../store/index'

// 配置选项
const config = {
  sampleSize: 5, // 采样数量
  throttleTime: 500, // 节流时间（ms）
  historySize: 5, // 历史数据大小，只保留最新的5条
  enablePacketLoss: true, // 是否启用丢包率监测
  packetLossTestCount: 5, // 丢包率测试次数
  enableJitterMonitoring: false, // 是否启用网络抖动监测
  warningThreshold: { // 网络质量预警阈值
    latency: 500, // 延迟阈值（ms）
    packetLoss: 10, // 丢包率阈值（%）
    bandwidth: 0.5 // 带宽阈值（Mbps）
  }
}

// 浏览器兼容性检测
const isConnectionSupported = !!navigator.connection || !!navigator.mozConnection || !!navigator.webkitConnection
let connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection

// 存储状态
const state = {
  lastMeasurementTime: 0,
  latencySamples: [],
  bandwidthSamples: [],
  packetLossSamples: [],
  jitterSamples: [],
  networkHistory: [],
  networkOperations: {
    total: 0,
    success: 0,
    failure: 0
  },
  throttleControl: {
    count: 0,
    lastResetTime: Date.now()
  },
  warningCallbacks: []
}

// 检查是否可以执行网络监测
function canExecuteNetworkCheck () {
  const now = Date.now()
  // 每1秒重置一次计数器
  if (now - state.throttleControl.lastResetTime > 1000) {
    state.throttleControl.count = 0
    state.throttleControl.lastResetTime = now
  }

  // 1秒内最多执行1次
  if (state.throttleControl.count < 1) {
    state.throttleControl.count++
    return true
  }

  return false
}

// 从本地存储加载历史数据
function loadHistory () {
  try {
    // 检查localStorage是否可用
    if (typeof localStorage !== 'undefined') {
      const storedHistory = localStorage.getItem('networkQualityHistory')
      if (storedHistory) {
        const parsedHistory = JSON.parse(storedHistory)
        if (Array.isArray(parsedHistory)) {
          state.networkHistory.push(...parsedHistory.slice(-config.historySize))
        }
      }
    }
  } catch (error) {
    console.warn('加载网络历史数据失败:', error)
    // 本地存储不可用时，使用内存存储
  }
}

// 保存历史数据到本地存储
function saveHistory () {
  try {
    // 检查localStorage是否可用
    if (typeof localStorage !== 'undefined') {
      const historyToSave = state.networkHistory.slice(-config.historySize)
      localStorage.setItem('networkQualityHistory', JSON.stringify(historyToSave))
    }
  } catch (error) {
    console.warn('保存网络历史数据失败:', error)
    // 本地存储不可用时，使用内存存储
  }
}

// 监测网络类型
function getNetworkType () {
  if (connection) {
    return connection.type || 'Unknown'
  }

  // 备选方案：通过navigator.userAgent判断
  const ua = navigator.userAgent
  if (ua.includes('Mobile')) {
    return 'Mobile'
  } else if (ua.includes('Tablet')) {
    return 'Tablet'
  } else {
    return 'Desktop'
  }
}

// 记录网络操作结果
function recordNetworkOperation (success) {
  state.networkOperations.total++
  if (success) {
    state.networkOperations.success++
  } else {
    state.networkOperations.failure++
  }

  // 限制记录的操作数量，只保留最新的5条
  if (state.networkOperations.total > 5) {
    state.networkOperations.total = 5
    state.networkOperations.success = Math.floor(state.networkOperations.success * 0.2)
    state.networkOperations.failure = Math.floor(state.networkOperations.failure * 0.2)
  }
}

// 计算丢包率
function measurePacketLoss () {
  if (!config.enablePacketLoss) {
    return 0
  }

  if (state.networkOperations.total === 0) {
    return 0
  }

  // 计算丢包率
  const packetLoss = (state.networkOperations.failure / state.networkOperations.total) * 100

  // 添加到采样数组
  state.packetLossSamples.push(packetLoss)
  if (state.packetLossSamples.length > config.sampleSize) {
    state.packetLossSamples.shift()
  }

  // 计算平均丢包率（去除最大值和最小值）
  const sortedPacketLoss = [...state.packetLossSamples].sort((a, b) => a - b)
  const filteredPacketLoss = sortedPacketLoss.slice(1, sortedPacketLoss.length - 1)
  const avgPacketLoss = filteredPacketLoss.length > 0
    ? filteredPacketLoss.reduce((sum, val) => sum + val, 0) / filteredPacketLoss.length
    : packetLoss

  return avgPacketLoss
}

// 计算网络抖动
function measureJitter () {
  if (!config.enableJitterMonitoring) {
    return 0
  }

  // 简单实现：基于延迟样本的标准差
  if (state.latencySamples.length < 2) {
    return 0
  }

  // 计算平均延迟
  const avgLatency = state.latencySamples.reduce((sum, val) => sum + val, 0) / state.latencySamples.length

  // 计算标准差
  const variance = state.latencySamples.reduce((sum, val) => sum + Math.pow(val - avgLatency, 2), 0) / state.latencySamples.length
  const jitter = Math.sqrt(variance)

  // 添加到采样数组
  state.jitterSamples.push(jitter)
  if (state.jitterSamples.length > config.sampleSize) {
    state.jitterSamples.shift()
  }

  // 计算平均抖动（去除最大值和最小值）
  const sortedJitter = [...state.jitterSamples].sort((a, b) => a - b)
  const filteredJitter = sortedJitter.slice(1, sortedJitter.length - 1)
  const avgJitter = filteredJitter.length > 0
    ? filteredJitter.reduce((sum, val) => sum + val, 0) / filteredJitter.length
    : jitter

  return avgJitter
}

// 直接从connection API获取网络性能数据
function getNetworkData () {
  let latency = Infinity
  let bandwidth = 0
  let packetLoss = 0
  let jitter = 0

  // 获取丢包率
  packetLoss = measurePacketLoss()

  if (connection) {
    // 从navigator.connection获取网络数据
    latency = connection.rtt || Infinity
    bandwidth = (connection.downlink || 0) * 10 // 转换为Mbps
  } else {
    // 备选方案：使用默认值或其他方法获取网络数据
    latency = 300 // 默认延迟（ms）
    bandwidth = 2 // 默认带宽（Mbps）
  }

  // 添加到采样数组
  state.latencySamples.push(latency)
  if (state.latencySamples.length > config.sampleSize) {
    state.latencySamples.shift()
  }

  state.bandwidthSamples.push(bandwidth)
  if (state.bandwidthSamples.length > config.sampleSize) {
    state.bandwidthSamples.shift()
  }

  // 计算平均延迟（去除最大值和最小值）
  const sortedLatencies = [...state.latencySamples].sort((a, b) => a - b)
  const filteredLatencies = sortedLatencies.slice(1, sortedLatencies.length - 1)
  const avgLatency = filteredLatencies.length > 0
    ? filteredLatencies.reduce((sum, val) => sum + val, 0) / filteredLatencies.length
    : latency

  // 计算平均带宽（去除最大值和最小值）
  const sortedBandwidths = [...state.bandwidthSamples].sort((a, b) => a - b)
  const filteredBandwidths = sortedBandwidths.slice(1, sortedBandwidths.length - 1)
  const avgBandwidth = filteredBandwidths.length > 0
    ? filteredBandwidths.reduce((sum, val) => sum + val, 0) / filteredBandwidths.length
    : bandwidth

  // 计算网络抖动
  jitter = measureJitter()

  return { latency: avgLatency, bandwidth: avgBandwidth, packetLoss, jitter }
}

// 监听网络连接变化
if (isConnectionSupported && connection && typeof connection.addEventListener === 'function') {
  connection.addEventListener('change', getNetWork)
}

// 安全重写原生方法
function safeOverride (target, method, overrideFn) {
  const originalMethod = target[method]
  if (typeof originalMethod === 'function') {
    target[method] = function (...args) {
      return overrideFn.call(this, originalMethod, ...args)
    }
  }
}

// 重写fetch方法，在每次网络请求时触发网络监测并记录结果
safeOverride(window, 'fetch', async function (originalFetch, ...args) {
  try {
    const result = await originalFetch.apply(this, args)
    recordNetworkOperation(true)
    getNetWork()
    return result
  } catch (error) {
    recordNetworkOperation(false)
    getNetWork()
    throw error
  }
})

// 重写XMLHttpRequest，在每次网络请求时触发网络监测并记录结果
safeOverride(XMLHttpRequest.prototype, 'open', function (originalOpen, ...args) {
  // 记录请求结果
  this.addEventListener('load', function () {
    recordNetworkOperation(this.status >= 200 && this.status < 400)
  })
  this.addEventListener('error', function () {
    recordNetworkOperation(false)
  })
  this.addEventListener('timeout', function () {
    recordNetworkOperation(false)
  })

  getNetWork()
  return originalOpen.apply(this, args)
})

// 重写localStorage和sessionStorage的方法，在操作本地存储时触发网络监测并记录结果
safeOverride(Storage.prototype, 'setItem', function (originalSetItem, ...args) {
  try {
    const result = originalSetItem.apply(this, args)
    recordNetworkOperation(true)
    getNetWork()
    return result
  } catch (error) {
    recordNetworkOperation(false)
    getNetWork()
    throw error
  }
})

safeOverride(Storage.prototype, 'getItem', function (originalGetItem, ...args) {
  try {
    const result = originalGetItem.apply(this, args)
    recordNetworkOperation(true)
    getNetWork()
    return result
  } catch (error) {
    recordNetworkOperation(false)
    getNetWork()
    throw error
  }
})

// 导出网络监测函数
export const netWorkQuality = getNetWork

// 导出配置选项，允许外部调整
export const setNetworkConfig = (newConfig) => {
  Object.assign(config, newConfig)
}

// 加载历史数据
loadHistory()

// 检查网络质量是否需要预警
function checkNetworkWarning (latency, bandwidth, packetLoss) {
  const warnings = []

  if (latency > config.warningThreshold.latency) {
    warnings.push(`延迟过高: ${Math.round(latency)}ms`)
  }

  if (bandwidth < config.warningThreshold.bandwidth) {
    warnings.push(`带宽过低: ${bandwidth.toFixed(2)}Mbps`)
  }

  if (packetLoss > config.warningThreshold.packetLoss) {
    warnings.push(`丢包率过高: ${packetLoss.toFixed(2)}%`)
  }

  return warnings
}

// 触发网络质量预警
function triggerNetworkWarning (warnings) {
  if (warnings.length > 0) {
    console.warn('网络质量预警:', warnings)
    // 调用注册的预警回调函数
    state.warningCallbacks.forEach(callback => {
      try {
        callback(warnings)
      } catch (error) {
        console.error('预警回调执行失败:', error)
      }
    })
  }
}

// 评估网络质量等级
function evaluateNetworkQuality (latency, bandwidth, rtt, downlink, packetLoss, jitter) {
  console.log(`延迟：${latency}ms, 带宽：${bandwidth}Mbps, RTT：${rtt}ms, 下行带宽：${downlink}Mbps, 丢包率：${packetLoss}%, 网络抖动：${jitter}ms`)
  // 综合考虑多个指标
  let score = 0

  // 延迟评分（越低越好）- 调整权重，让良好网络要求更严格
  if (latency <= 100) {
    score += 20
  } else if (latency <= 150) {
    score += 15
  } else if (latency <= 200) {
    score += 10
  } else if (latency <= 300) {
    score += 5
  } else if (latency <= 400) {
    score += 2
  } else {
    score += 0
  }

  // 带宽评分（越高越好）- 调整权重，提高良好网络的带宽要求
  if (bandwidth >= 5) {
    score += 20
  } else if (bandwidth >= 3) {
    score += 15
  } else if (bandwidth >= 2) {
    score += 10
  } else if (bandwidth >= 1.5) {
    score += 5
  } else if (bandwidth >= 1) {
    score += 2
  } else {
    score += 0
  }

  // RTT评分（越低越好）- 调整权重，要求更严格
  if (rtt <= 100) {
    score += 12
  } else if (rtt <= 150) {
    score += 8
  } else if (rtt <= 200) {
    score += 4
  } else if (rtt <= 300) {
    score += 1
  } else {
    score += 0
  }

  // 下行带宽评分（越高越好）- 调整权重，要求更严格
  if (downlink >= 5) {
    score += 12
  } else if (downlink >= 3) {
    score += 8
  } else if (downlink >= 2) {
    score += 4
  } else if (downlink >= 1.5) {
    score += 1
  } else {
    score += 0
  }

  // 丢包率评分（越低越好）- 要求更严格
  if (packetLoss <= 0.5) {
    score += 18
  } else if (packetLoss <= 1) {
    score += 15
  } else if (packetLoss <= 3) {
    score += 10
  } else if (packetLoss <= 5) {
    score += 5
  } else if (packetLoss <= 10) {
    score += 2
  } else {
    score += 0
  }

  // 网络抖动评分（越低越好）- 要求更严格
  if (jitter <= 30) {
    score += 18
  } else if (jitter <= 50) {
    score += 15
  } else if (jitter <= 80) {
    score += 10
  } else if (jitter <= 120) {
    score += 5
  } else if (jitter <= 150) {
    score += 2
  } else {
    score += 0
  }

  console.log(`网络质量评分:${score}`)

  // 检查网络质量预警
  const warnings = checkNetworkWarning(latency, bandwidth, packetLoss)
  if (warnings.length > 0) {
    triggerNetworkWarning(warnings)
  }

  // 根据总分评估网络质量 - 提高Good级别的门槛，要求更严格
  if (score >= 80) {
    return 'Good'
  } else if (score >= 70) {
    return 'Unstable'
  } else {
    return 'Poor'
  }
}

// 主网络监测函数
export async function getNetWork () {
  try {
    // 检查是否可以执行网络监测（1秒内最多1次）
    if (!canExecuteNetworkCheck()) {
      return
    }

    const currentTime = Date.now()
    // 确保不会过于频繁地进行测量
    if (currentTime - state.lastMeasurementTime < config.throttleTime) {
      return
    }
    state.lastMeasurementTime = currentTime

    if (navigator.onLine) {
      // 直接从connection API获取网络性能数据
      const { latency, bandwidth, packetLoss, jitter } = getNetworkData()

      // 获取连接类型信息
      let connectionType = 'Unknown'
      let effectiveType = 'Unknown'
      let downlink = 0
      let rtt = 0

      if (connection) {
        connectionType = connection.type || 'Unknown'
        effectiveType = connection.effectiveType || 'Unknown'
        downlink = connection.downlink || 0
        rtt = connection.rtt || 0
      }

      // 监测网络类型
      const networkType = getNetworkType()

      // 综合评估网络质量
      const quality = evaluateNetworkQuality(latency, bandwidth, rtt, downlink, packetLoss, jitter)

      // 构建网络信息对象
      const networkInfo = {
        quality,
        latency: Math.round(latency),
        bandwidth: Math.round(bandwidth * 100) / 100,
        packetLoss: Math.round(packetLoss * 100) / 100,
        jitter: Math.round(jitter),
        connectionType,
        effectiveType,
        downlink,
        rtt,
        networkType,
        timestamp: currentTime
      }

      // 保存网络质量信息到store
      $store.commit('set_effectiveType', quality)

      // 保存到历史数据
      state.networkHistory.push(networkInfo)
      // 只保留最新的5条
      if (state.networkHistory.length > 5) {
        state.networkHistory.splice(0, state.networkHistory.length - 5)
      }

      // 保存历史数据到本地存储
      saveHistory()
    } else {
      const offlineInfo = {
        quality: 'Offline',
        timestamp: Date.now()
      }

      $store.commit('set_effectiveType', 'Offline')

      // 保存到历史数据
      state.networkHistory.push(offlineInfo)
      // 只保留最新的5条
      if (state.networkHistory.length > 5) {
        state.networkHistory.splice(0, state.networkHistory.length - 5)
      }

      // 保存历史数据到本地存储
      saveHistory()
    }
  } catch (error) {
    console.error('网络监测失败:', error)
    // 异常情况下返回合理结果
    if (navigator.onLine) {
      $store.commit('set_effectiveType', 'Poor')
    } else {
      $store.commit('set_effectiveType', 'Offline')
    }
  }
}

// 导出网络质量趋势分析函数
export const getNetworkTrend = (hours = 24) => {
  const cutoffTime = Date.now() - (hours * 60 * 60 * 1000)
  const recentHistory = state.networkHistory.filter(item => item.timestamp >= cutoffTime)

  // 按时间分组
  const hourlyData = {}
  recentHistory.forEach(item => {
    const hour = new Date(item.timestamp).toISOString().slice(0, 13)
    if (!hourlyData[hour]) {
      hourlyData[hour] = []
    }
    hourlyData[hour].push(item)
  })

  // 计算每小时的平均网络质量
  const trendData = Object.entries(hourlyData).map(([hour, items]) => {
    const avgLatency = items.reduce((sum, item) => sum + (item.latency || 0), 0) / items.length
    const avgBandwidth = items.reduce((sum, item) => sum + (item.bandwidth || 0), 0) / items.length
    const avgPacketLoss = items.reduce((sum, item) => sum + (item.packetLoss || 0), 0) / items.length
    const avgJitter = items.reduce((sum, item) => sum + (item.jitter || 0), 0) / items.length

    return {
      hour,
      avgLatency: Math.round(avgLatency),
      avgBandwidth: Math.round(avgBandwidth * 100) / 100,
      avgPacketLoss: Math.round(avgPacketLoss * 100) / 100,
      avgJitter: Math.round(avgJitter),
      sampleCount: items.length
    }
  })

  return trendData
}

// 导出网络质量预警注册函数
export const registerNetworkWarningCallback = (callback) => {
  if (typeof callback === 'function') {
    state.warningCallbacks.push(callback)
  }
}

// 导出网络质量预警取消函数
export const unregisterNetworkWarningCallback = (callback) => {
  const index = state.warningCallbacks.indexOf(callback)
  if (index > -1) {
    state.warningCallbacks.splice(index, 1)
  }
}



// WEBPACK FOOTER //
// ./src/components/bloom-proctor/public/netWork.js